import React, { useState, useEffect } from 'react'
import Calendar from 'react-calendar'
import { ModalWrapper } from '../../../components/modalWrapper'

interface DobModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (dob: Date) => void
  dob?: string | null
  title?: string
  description?: string
  confirmButtonText?: string
  isLoading?: boolean
  serverError?: string | null
}

const DobModal: React.FC<DobModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  dob = null,
  title = 'Enter Date of Birth',
  description = 'Please select the date of birth.',
  confirmButtonText = 'Confirm',
  isLoading = false,
  serverError = null,
}) => {
  const [selectedDob, setSelectedDob] = useState<Date | null>(null)
  const [calendarDate, setCalendarDate] = useState(new Date())
  const [showServerError, setShowServerError] = useState(false)

  useEffect(() => {
    if (isOpen && dob) {
      const dobDate = new Date(dob)
      setSelectedDob(dobDate)
      setCalendarDate(dobDate)
    }
  }, [isOpen, dob])

  useEffect(() => {
    if (serverError) {
      setShowServerError(true)
    }
  }, [serverError])

  const calendarMonth = calendarDate.getMonth()
  const calendarYear = calendarDate.getFullYear()
  const currentYear = new Date().getFullYear()

  const yearOptions = Array.from({ length: 100 }, (_, i) => currentYear - i)
  const monthOptions = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = parseInt(e.target.value)
    const newDate = new Date(calendarYear, newMonth, 1)
    setCalendarDate(newDate)
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(e.target.value)
    const newDate = new Date(newYear, calendarMonth, 1)
    setCalendarDate(newDate)
  }

  const handleConfirm = () => {
    if (selectedDob) {
      onConfirm(selectedDob)
    }
  }

  const handleClose = () => {
    setSelectedDob(null)
    setCalendarDate(new Date())
    setShowServerError(false)
    onClose()
  }

  const formatDobDisplay = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (!isOpen) return null

  return (
    <ModalWrapper
      title={title}
      hideModal={handleClose}
      xclose={true}
      width="500px"
      height="auto"
    >
      <div style={{ padding: '20px' }}>
        <p
          style={{
            marginBottom: '15px',
            color: '#4b5563',
            fontSize: '14px',
          }}
        >
          {description}
        </p>

        {selectedDob && (
          <div
            style={{
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: '#e0f2fe',
              borderRadius: '4px',
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#0369a1',
            }}
          >
            Selected: {formatDobDisplay(selectedDob)}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '15px',
            justifyContent: 'center',
          }}
        >
          <div style={{ flex: 1 }}>
            <label
              htmlFor="month-select"
              style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
              }}
            >
              Month
            </label>
            <select
              id="month-select"
              value={calendarMonth}
              onChange={handleMonthChange}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
            >
              {monthOptions.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <label
              htmlFor="year-select"
              style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
              }}
            >
              Year
            </label>
            <select
              id="year-select"
              value={calendarYear}
              onChange={handleYearChange}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="calendar-wrapper" style={{ marginBottom: '20px' }}>
          <Calendar
            maxDate={new Date()}
            onChange={(value: any) => {
              setSelectedDob(value)
              setShowServerError(false)
            }}
            value={selectedDob}
            activeStartDate={calendarDate}
            onActiveStartDateChange={({ activeStartDate }) =>
              setCalendarDate(activeStartDate)
            }
          />
        </div>

        {serverError && showServerError && (
          <div
            style={{
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '4px',
              color: '#dc2626',
              fontSize: '14px',
            }}
          >
            {serverError}
          </div>
        )}

        <div className="modal__buttons">
          <button
            className="button button-drive-america"
            onClick={handleConfirm}
            disabled={!selectedDob || isLoading}
          >
            {isLoading ? 'Processing...' : confirmButtonText}
          </button>
          <button
            className="button button-drive-america"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  )
}

export default DobModal
